import { parsePairs, splitList } from '~/components/common/attribute-editor/attribute-value';
import { CreatableChipInput } from '~/components/common/creatable-chip-input';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import type { OptimizationContentType } from '~/types/enrichment';

/** Above this length a named sub-value gets a textarea instead of a single-line
 *  input — long prose in a one-line field is unusable. */
const TEXTAREA_THRESHOLD = 90;

interface Props {
  contentType: OptimizationContentType;
  /** Current draft, in the same wire format the API expects back. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** `data-testid` root; derives `-chips`, `-textarea`, `-fields`, `-field-{key}` (+ `-label`, `-input`). */
  testId?: string;
}

/** Kebab-cases a sub-value key so it can key a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Edits one enriched attribute in the shape its content type implies: a
 * creatable multi-value field for lists, one labelled control per key for
 * named sub-values (single-line or textarea depending on length), and a plain
 * textarea for prose. Emits the serialised wire string on every change, so the
 * parent only ever holds one draft string.
 */
export function AttributeEditor({
  contentType,
  value,
  onChange,
  disabled = false,
  testId = 'attribute-editor',
}: Props) {
  // --- List-valued: comma-separated tags, pipe-separated bullets ---
  if (contentType === 'tags' || contentType === 'bullets') {
    const separator = contentType === 'tags' ? ',' : '|';
    const joiner = contentType === 'tags' ? ', ' : ' | ';
    return (
      <CreatableChipInput
        testId={`${testId}-chips`}
        values={splitList(value, separator)}
        onChange={(next) => {
          onChange(next.join(joiner));
        }}
        disabled={disabled}
        placeholder={contentType === 'tags' ? 'Add a tag' : 'Add a line'}
      />
    );
  }

  // --- Named sub-values: one control per key, rebuilt as `Key: Value | …` ---
  if (contentType === 'attributes') {
    const pairs = parsePairs(value);
    if (pairs.length === 0) {
      return (
        <Textarea
          data-testid={`${testId}-textarea`}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          rows={3}
          className="bg-white text-sm"
        />
      );
    }
    return (
      <div className="flex flex-col gap-2" data-testid={`${testId}-fields`}>
        {pairs.map((pair, i) => {
          const update = (next: string) => {
            const copy = pairs.map((p, index) => (index === i ? { ...p, value: next } : p));
            onChange(copy.map((p) => `${p.key}: ${p.value}`).join(' | '));
          };
          const fieldTestId = `${testId}-field-${toTestIdSlug(pair.key)}`;
          return (
            <div
              key={`${pair.key}-${String(i)}`}
              className="flex flex-col gap-1"
              data-testid={fieldTestId}
            >
              <span className="text-muted-foreground text-xs" data-testid={`${fieldTestId}-label`}>
                {pair.key}
              </span>
              {pair.value.length > TEXTAREA_THRESHOLD ? (
                <Textarea
                  data-testid={`${fieldTestId}-input`}
                  value={pair.value}
                  disabled={disabled}
                  onChange={(e) => {
                    update(e.target.value);
                  }}
                  rows={3}
                  className="bg-white text-sm"
                />
              ) : (
                <Input
                  data-testid={`${fieldTestId}-input`}
                  value={pair.value}
                  disabled={disabled}
                  onChange={(e) => {
                    update(e.target.value);
                  }}
                  className="bg-white text-sm"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // --- JSON object: one control per key, re-serialised with the same keys ---
  if (contentType === 'structured-json') {
    // Values are typed `unknown`, not `string`: a real payload can carry nulls,
    // numbers or nested objects, and `val.length` on those either throws or
    // silently feeds a non-string into an input.
    let entries: [string, unknown][] = [];
    let parsed = true;
    try {
      const obj = JSON.parse(value) as unknown;
      if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
        entries = Object.entries(obj as Record<string, unknown>);
      } else {
        parsed = false;
      }
    } catch {
      parsed = false;
    }
    // Only string values get a control; anything else is round-tripped untouched
    // so editing one field can't flatten a nested structure.
    const editable = entries.filter(([, val]) => typeof val === 'string');

    if (parsed && editable.length > 0) {
      // Match the source's formatting so a save doesn't reflow the whole value:
      // a compact payload stays compact, a pretty-printed one stays indented.
      const wasPretty = value.includes('\n');
      return (
        <div className="flex flex-col gap-2" data-testid={`${testId}-fields`}>
          {editable.map(([key, val]) => {
            const text = val as string;
            const update = (next: string) => {
              const copy = entries.map(([k, v]): [string, unknown] =>
                k === key ? [k, next] : [k, v],
              );
              onChange(
                wasPretty
                  ? JSON.stringify(Object.fromEntries(copy), null, 2)
                  : JSON.stringify(Object.fromEntries(copy)),
              );
            };
            const fieldTestId = `${testId}-field-${toTestIdSlug(key)}`;
            return (
              <div key={key} className="flex flex-col gap-1" data-testid={fieldTestId}>
                <span
                  className="text-muted-foreground text-xs"
                  data-testid={`${fieldTestId}-label`}
                >
                  {key}
                </span>
                {text.length > TEXTAREA_THRESHOLD ? (
                  <Textarea
                    data-testid={`${fieldTestId}-input`}
                    value={text}
                    disabled={disabled}
                    onChange={(e) => {
                      update(e.target.value);
                    }}
                    rows={4}
                    className="bg-white text-sm"
                  />
                ) : (
                  <Input
                    data-testid={`${fieldTestId}-input`}
                    value={text}
                    disabled={disabled}
                    onChange={(e) => {
                      update(e.target.value);
                    }}
                    className="bg-white text-sm"
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }
    // Unparseable JSON, an array, or an object with no string fields falls
    // through to the raw textarea rather than being silently reshaped.
  }

  // --- `text` carrying multiple pipe-delimited items behaves as a list ---
  if (contentType === 'text' && splitList(value, '|').length > 1) {
    return (
      <CreatableChipInput
        testId={`${testId}-chips`}
        values={splitList(value, '|')}
        onChange={(next) => {
          onChange(next.join(' | '));
        }}
        disabled={disabled}
        placeholder="Add a line"
      />
    );
  }

  // --- Prose ---
  return (
    <Textarea
      data-testid={`${testId}-textarea`}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      rows={value.length > 240 ? 8 : 4}
      className="bg-white text-sm"
    />
  );
}
